import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
} from "@angular/core";
import { OrderSearchParams } from "src/app/shared/models/order.model";
import { DataElement, Doctor } from "src/app/shared/models/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { keyCodes } from "src/app/shared/models/key-codes.model";
import { AuthService } from "src/app/shared/services/auth.service";
import { Popover, PopoverModule } from "primeng/popover";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import {
  SELECT_INBOX_RESULTS_PER_PAGE,
  UI__ROUTE_NAVIGATION,
  UI__SET_LANG,
} from "src/app/shared/ngrx/ngrx-actions";
import {
  clinicList,
  deliverersList,
  doctorList,
  inboxResultsPerPage,
  rxItems,
  stageList,
  taskUsersList,
  urgencyList,
} from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { InputWrapComponent } from "src/app/shared/components/ui/input-wrap.component";
import { MultiSelectModule } from "primeng/multiselect";
import { ClearMultiComponent } from "src/app/shared/components/ui/clear-multi.component";
import { deepStrictEqual } from "assert";
import { UiUser } from "src/app/shared/models/ui-user.model";
import { BooleanWrapperObject } from "aws-sdk/clients/iot";

@Component({
  selector: "inbox-filters",
  templateUrl: "./inbox-filters.component.html",
  styleUrls: ["./inbox-filters.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    InputWrapComponent,
    TranslateModule,
    SelectModule,
    MultiSelectModule,
    PopoverModule,
    ClearMultiComponent,
    ToggleSwitchModule,
  ],
  standalone: true,
})
export class InboxFiltersComponent implements OnInit {
  @Output() onSearch = new EventEmitter<OrderSearchParams>();
  @Output() onResultsPerPageChanged = new EventEmitter();

  @ViewChild("filterBy") filterByElement;
  @ViewChild("clinic") clinicElement;
  @ViewChild("doctor") doctorElement;
  @ViewChild("orderIdMmdInput") orderIdMmdInput: any;

  searchParams: OrderSearchParams;
  orderIdMmd: string;
  selectedClinicList: DataElement[] = [];
  selectedDoctorList: DataElement[] = [];
  selectedRxItemList: DataElement[] = [];
  selectedDeliverer: DataElement;
  selectedTaskOwnerList: UiUser[] = [];
  selectedStage: DataElement;
  selectedUrgency: DataElement;
  selectedChatFilter: DataElement;
  patientName: string;
  orderDateFrom: Date;
  orderDateTo: Date;
  ordTik: string;
  ordScanNum: string;
  calLocale = {};
  favoriteSw: boolean = false;
  tasksSw: boolean = false;
  inhouseSw: boolean = false;
  rxSw: boolean = false;
  qcModeSw: boolean = false;
  formBind = this;
  resultsPerPage: number[] = [10, 20, 50];
  displayPatientFields: boolean;
  displayUrgency: boolean;
  displayTasks: boolean;
  displayInhouse: boolean;
  displayRX: boolean;
  displayClinic: boolean;
  displayRXitems: boolean;
  displayTaskOwners: boolean;
  displayDeliverers: boolean;
  displayQCMode: boolean;
  plantUserSw: boolean;
  labUserSw: boolean;
  allFilters: DataElement[] = [];
  displayedFilterList: DataElement[] = [];
  selectedFilters: DataElement[] = [];
  chatFilterOptions: DataElement[] = [];
  filteredDoctorList: DataElement[] = [];
  destroy$ = new Subject();
  curResultsPerPage: number;
  stageList$ = this.store.select(stageList);
  urgencyList$ = this.store.select(urgencyList);
  clinicList$ = this.store.select(clinicList);
  rxItemList$ = this.store.select(rxItems);
  taskOwnerList$ = this.store.select(taskUsersList);
  delivererList$ = this.store.select(deliverersList);
  doctorList: Doctor[];

  get displayDoctorList(): Doctor[] {
    return this.selectedClinicList.length
      ? this.filteredDoctorList
      : this.doctorList;
  }

  constructor(
    private readonly store: Store,
    private translateService: TranslateService,
    private authService: AuthService,
    private actions$: Actions,
    private cd: ChangeDetectorRef
  ) {
    this.translateService.get("calLocale").subscribe((data) => {
      this.calLocale = data;
    });

    this.displayPatientFields = this.authService.currentUser.org != "PLANT";
    this.displayUrgency = this.authService.currentUser.org != "CLINIC";
    this.plantUserSw = this.authService.currentUser.org == "PLANT";
    this.labUserSw = this.authService.currentUser.org == "LAB";
    this.displayTasks =
      this.authService.currentUser.authDetails.getBooleanValue(
        "ORDERS.CHAT_TASKS_SW"
      );
    this.displayRX = false;
    this.displayInhouse = this.labUserSw;
    this.displayClinic = this.authService.currentUser.org != "CLINIC" || this.authService.currentUser.isMultiClinicUser;
    this.displayRXitems = this.authService.currentUser.org != "CLINIC";
    this.displayTaskOwners = this.authService.currentUser.org != "CLINIC";
    this.displayQCMode = this.authService.currentUser.authDetails.getBooleanValue(
      "ORDERS.QC_MODE_ENABLED"
    );
    this.displayDeliverers = this.authService.currentUser.authDetails.getBooleanValue(
      "ORDERS.DELIVERER_FILTER_ENABLED"
    );

    this.chatFilterOptions.push(
      new DataElement(
        "ALL_MSG",
        this.translateService.instant("translate.inbox.chatFilterOpt.HAS_MSG")
      )
    );
    this.chatFilterOptions.push(
      new DataElement(
        "UNREAD_BY_ME",
        this.translateService.instant(
          "translate.inbox.chatFilterOpt.HAS_UNREAD"
        )
      )
    );

    this.actions$
      .pipe(
        ofType(UI__ROUTE_NAVIGATION),
        takeUntil(this.destroy$),
        map(({ data }) => {
          if (data.tabId == "inbox") {
            this.clearSearch();
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(UI__SET_LANG),
        takeUntil(this.destroy$),
        map(({ }) => {
          setTimeout(() => {
            this.initFilterOptions();
            this.cd.detectChanges();
          }, 200);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.store
      .select(inboxResultsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.curResultsPerPage = sm;
      });

    this.store
      .select(doctorList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.doctorList = sm;
      });

    this.initFilterOptions();

    if (this.labUserSw || this.plantUserSw) {
      this.store.dispatch(
        SELECT_INBOX_RESULTS_PER_PAGE({
          inboxResultsPerPage: 50,
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  initFilterOptions() {
    let orderId = new DataElement(
      "orderId",
      this.translateService.instant("translate.general.orderIdMmd")
    );
    let clinic = new DataElement(
      "clinic",
      this.translateService.instant("translate.general.clinic")
    );
    let doctor = new DataElement(
      "doctor",
      this.translateService.instant("translate.general.doctor")
    );
    let patient = new DataElement(
      "patient",
      this.translateService.instant("translate.general.patient")
    );
    let stage = new DataElement(
      "stage",
      this.translateService.instant("translate.general.stage")
    );
    let urgency = new DataElement(
      "urgency",
      this.translateService.instant("translate.general.urgency")
    );
    let favorites = new DataElement(
      "favorites",
      this.translateService.instant("translate.general.favorites")
    );
    let tasks = new DataElement(
      "tasks",
      this.translateService.instant("translate.general.tasks")
    );
    let inhouse = new DataElement(
      "inhouse",
      this.translateService.instant("translate.general.inHouse")
    );
    let rx = new DataElement(
      "rx",
      this.translateService.instant("translate.general.downloadRxFilter")
    );
    let ordTik = new DataElement(
      "ordTik",
      this.translateService.instant("translate.general.ordTik")
    );
    let ordScanNum = new DataElement(
      "ordScanNum",
      this.translateService.instant("translate.general.ordScanNum")
    );
    let chat = new DataElement(
      "chat",
      this.translateService.instant("translate.inbox.chatFilter")
    );
    let deliverer = new DataElement(
      "deliverer",
      this.translateService.instant("translate.general.deliverer")
    );
    let qcMode = new DataElement(
      "qcMode",
      this.translateService.instant("translate.general.qcMode")
    );

    this.allFilters.push(
      orderId,
      stage,
      urgency,
      favorites,
      clinic,
      doctor,
      patient,
      chat
    );

    if (this.authService.currentUser.org == "PLANT") {
      this.displayedFilterList.push(stage, urgency, favorites, clinic, doctor);
    } else {
      this.displayedFilterList.push(orderId, stage, doctor, urgency, favorites);
    }

    if (this.displayTasks) {
      this.allFilters.push(tasks);
      this.displayedFilterList.push(tasks);
    }

    if (this.displayInhouse) {
      this.allFilters.push(inhouse);
      this.displayedFilterList.push(inhouse);
    }

    if (this.displayRX) {
      this.allFilters.push(rx);
      this.displayedFilterList.push(rx);
    }

    if (this.displayDeliverers) {
      this.allFilters.push(deliverer);
      this.displayedFilterList.push(deliverer);
    }

    if (this.displayQCMode) {
      this.allFilters.push(qcMode);
      this.displayedFilterList.push(qcMode);
    }

    if (this.authService.currentUser.org == "LAB") {
      this.displayedFilterList.push(ordTik, ordScanNum);
    }

    this.initSelectedFilters();
  }

  initSelectedFilters() {
    if (this.authService.currentUser.org == "PLANT") {
      this.selectedFilters = [this.allFilters.find((f) => f.id == "orderId")];
    } else if (this.authService.currentUser.org == "CLINIC") {
      this.selectedFilters = [
        this.allFilters.find((f) => f.id == "patient"),
        this.allFilters.find((f) => f.id == "orderId"),
      ];
    } else {
      this.selectedFilters = [
        this.allFilters.find((f) => f.id == "patient"),
        this.allFilters.find((f) => f.id == "clinic"),
      ];
    }
  }

  clearSearch() {
    this.orderIdMmd = null;
    this.selectedClinicList = [];
    this.selectedDoctorList = [];
    this.selectedRxItemList = [];
    this.selectedTaskOwnerList = [];
    this.patientName = null;
    this.orderDateFrom = null;
    this.orderDateTo = null;
    this.selectedStage = null;
    this.favoriteSw = false;
    this.tasksSw = false;
    this.inhouseSw = false;
    this.selectedUrgency = null;
    this.rxSw = false;
    this.ordTik = null;
    this.ordScanNum = null;
    this.selectedChatFilter = null;
    this.selectedDeliverer = null;
    this.initSelectedFilters();
    this.search();
  }

  search() {
    this.searchParams = {
      orderIdMmd: this.orderIdMmd,
      clinicList: this.selectedClinicList,
      doctorList: this.selectedDoctorList,
      rxItemList: this.selectedRxItemList,
      taskOwnerList: this.selectedTaskOwnerList,
      orderFromDate: this.orderDateFrom,
      orderToDate: this.orderDateTo,
      patientName: this.patientName,
      stage: this.selectedStage,
      searchId: null,
      summaryCode: null,
      chatCode: null,
      favoriteSw: this.favoriteSw,
      tasksSw: this.tasksSw,
      urgency: this.selectedUrgency,
      rxSw: this.rxSw,
      ordTik: this.ordTik,
      ordScanNum: this.ordScanNum,
      chatMsg: this.selectedChatFilter,
      inhouseSw: this.inhouseSw,
      qcModeSw: this.qcModeSw,
      deliverer: this.selectedDeliverer,
    };

    this.onSearch.emit(this.searchParams);
  }

  selectResultsPerPage(resultsPerPage: number, overlaypanel: Popover) {
    overlaypanel.hide();
    this.store.dispatch(
      SELECT_INBOX_RESULTS_PER_PAGE({
        inboxResultsPerPage: resultsPerPage,
      })
    );
    this.onResultsPerPageChanged.emit();
  }

  onKeyPress(e: any) {
    if (e.keyCode == keyCodes.enter) {
      this.search();
    }
  }

  onFieldValueChanged(
    e: any,
    closeClinic: boolean = false,
    closeDoctor: boolean = false
  ) {
    if (this.clinicElement && closeClinic) {
      this.clinicElement.hide();
      this.filterDoctors();
    }
    if (this.doctorElement && closeDoctor) this.doctorElement.hide();

    this.search();
  }

  filterDoctors() {
    if (this.selectedClinicList.length) {
      this.filteredDoctorList = this.doctorList?.filter((d) => {
        let inClinic = false;
        for (let i = 0; i < d.clinicList.length; i++) {
          inClinic =
            this.selectedClinicList.find(
              (c) => c.id.toString() == d.clinicList[i]
            ) != null;
          if (inClinic) break;
        }
        return inClinic;
      });
    } else {
      this.filteredDoctorList = [];
    }
  }

  onFilterByChanged(e: any) {
    (e.originalEvent as MouseEvent)?.stopPropagation();
    this.filterByElement.hide();

    if (!this.isFilterSelected("clinic")) this.selectedClinicList = [];
    if (!this.isFilterSelected("doctor")) this.selectedDoctorList = [];
    if (!this.isFilterSelected("rxItem")) this.selectedRxItemList = [];
    if (!this.isFilterSelected("taskOwner")) this.selectedTaskOwnerList = [];
    if (!this.isFilterSelected("patient")) this.patientName = null;
    if (!this.isFilterSelected("stage")) this.selectedStage = null;
    if (!this.isFilterSelected("urgency")) this.selectedUrgency = null;
    if (!this.isFilterSelected("favorites")) this.favoriteSw = false;
    if (!this.isFilterSelected("inhouse")) this.inhouseSw = false;
    if (!this.isFilterSelected("tasks")) this.tasksSw = false;
    if (!this.isFilterSelected("rx")) this.rxSw = false;
    if (!this.isFilterSelected("ordScanNum")) this.ordScanNum = null;
    if (!this.isFilterSelected("ordTik")) this.ordTik = null;
  }

  isFilterSelected(id: string): boolean {
    return this.selectedFilters.findIndex((f) => f.id == id) != -1;
  }

  focusOrderIdInput() {
    this.orderIdMmd = null;
    if (this.orderIdMmdInput && this.orderIdMmdInput.nativeElement) {
      setTimeout(() => {
        this.orderIdMmdInput.nativeElement.focus();
      }, 0);
    }
  }
}
